'use client';
import { createContext, ReactNode, Dispatch } from 'react';

import { useContextBroker } from '../ContextBroker/useContextBroker';
import { BrokerAction } from '../ContextBroker/ContextBroker';

import { PaymentCustomer, PaymentMethod } from '@/types/Payment';
import { noop } from '@/util/misc';
import { CACHE_TIME } from '@/const/cache';

export type PaymentContextValue = {
  customer: PaymentCustomer | null;
  method: PaymentMethod | null;
};

export type PaymentBrokerActions = {
  refreshPayment: () => Promise<PaymentContextValue>;
};

type PaymentBrokeredValue = {
  value: PaymentContextValue;
  actions: PaymentBrokerActions;
};

export const INITIAL_PAYMENT_VALUE = {
  customer: null,
  method: null
};

export const PaymentContext = createContext<PaymentBrokeredValue>({
  value: INITIAL_PAYMENT_VALUE,
  actions: {
    refreshPayment: noop
  }
});

type PaymentProviderProps = {
  /** Put the component tree in here. Server components are supported */
  children: ReactNode;

  dispatch: Dispatch<BrokerAction>;
};

function PaymentProvider(props: PaymentProviderProps) {
  const { children, dispatch } = props;

  const brokerValue = useContextBroker();

  const decoratedValue: PaymentBrokeredValue = {
    value: brokerValue.payment,
    actions: {
      refreshPayment: async (): Promise<PaymentContextValue> => {
        /*
          NOTE: This logic needs to be mirrored in PaymentContext.hydrator
          since we need separate code running on server and client
          This is client side code
        */
        const paymentContextValue: PaymentContextValue = {
          customer: null,
          method: null
        };

        const response = await fetch(`/api/payment/customer`, {
          method: 'GET',
          headers: {
            'content-type': 'application/json'
          },
          next: { revalidate: CACHE_TIME.NO_CACHE }
        });

        const json = (await response.json()) as {
          success: boolean;
          data: PaymentCustomer;
        };

        if (json.data) {
          paymentContextValue.customer = json.data;
        }

        const paymentMethodResponse = await fetch(`/api/payment/method`, {
          method: 'GET',
          headers: {
            'content-type': 'application/json'
          },
          next: { revalidate: CACHE_TIME.NO_CACHE }
        });

        const paymentJson = (await paymentMethodResponse.json()) as {
          data: PaymentMethod;
        };

        if (paymentJson.data) {
          paymentContextValue.method = paymentJson.data;
        }

        dispatch({
          type: 'PAYMENT_REFRESH',
          newContextValue: paymentContextValue
        });

        return paymentContextValue;
      }
    }
  };

  return (
    <PaymentContext.Provider value={decoratedValue}>
      {children}
    </PaymentContext.Provider>
  );
}

export default PaymentProvider;
